/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "react-query";
import axios from "axios";
import { useState, useRef } from "react";
import "./results.scss";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { ApiEndpoint } from "../components/utils";
import AppHeader from "../components/appheader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { BrandColors } from "../components/utils";
import { Tooltip } from "@mui/material";
import {
  formatResultsData,
  questionCatDescriptions,
} from "../components/utils";

import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import NetworkError from "../components/networkError";
import { getAppInfo, getUserInfo } from "../components/user";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

interface UserStats {
  headline?: string;
  sat_summary?: any;
  act_summary?: any;
  act_answers?: any;
  sat_answers?: any;
  summary_tables?: any;
  summary_tables_1?: any;
  summary_tables_2?: any;
  decision?: string;
  userName: string;
  testDate: string;
}

interface AnswerRow {
  answer: string;
  correct: string;
  type: string;
}

const TooltipText = (text: string) => {
  return <p>{text}</p>;
};

const ExamResults = () => {
  dayjs.extend(utc);
  const [data, setdata] = useState<UserStats | null>();
  const [hasError, setHasError] = useState(false);
  const userData = getUserInfo();
  const location = useLocation();
  const testDate = useRef<string | null | any>(null);
  const userName = useRef<string | null>(null);
  const { exam_id, userID, passedExamData, token, snapshotID, admin } =
    location.state || {};
  const examData = passedExamData
    ? passedExamData
    : getAppInfo()?.currentExamData;

  const { isLoading, error } = useQuery("examSATResultsPageData", async () => {
    let payload = {};
    if (userID) {
      payload = {
        token: token,
        exam_id: exam_id,
        userID: userID,
        snapshotID: snapshotID,
      };
    } else {
      payload = {
        token: userData?.token,
        exam_id: examData?.examId,
        snapshotID: snapshotID,
      };
    }
    const resp = await axios.post(
      ApiEndpoint + "wp-json/satapp-api/v1/testResults",
      payload,
    );
    if (!resp) {
      setHasError(true);
      return;
    }
    const stats: any = formatResultsData(
      resp.data,
      examData?.verbalScoringMatrix,
      examData?.mathScoringMatrix,
      examData?.verbalMinScore,
      examData?.mathMinScore,
    );

    stats.summary_tables_1 = [
      stats.summary_tables[0],
      stats.summary_tables[1],
      stats.summary_tables[2],
      stats.summary_tables[3],
    ];

    testDate.current = dayjs
      .utc(resp.data[0].snapshotDatetime)
      .local()
      .format("MM/DD/YYYY h:ma");

    // set username for results page
    userName.current = resp.data.userName;
    setdata(stats);
  });

  if (isLoading || !data?.summary_tables_1) return <Loader />;
  if (error) return <LoadError />;

  // parse out the current exam name
  const currentExam = passedExamData
    ? passedExamData
    : getAppInfo()?.examData?.filter(
        (items) => items.examId == getAppInfo()?.currentExamData?.examId,
      )[0];

  return (
    <div className="resultsPage">
      <div className="pageHeader">
        {!admin && <AppHeader timer={false} home={true} />}
      </div>
      <div className="pageContent results">
        <h2>Your Results</h2>
        <h3>
          <span>Name:</span>{" "}
          {userName.current
            ? userName.current
            : `${userData?.first_name} ${userData?.last_name}`}{" "}
          <br />
          <span>Test Date:</span>{" "}
          {testDate.current ? testDate.current : "unavailable"}
          <br />
          <span> Test Name:</span> {currentExam?.examName}
        </h3>

        <div className="resultsHolder">
          <div className="resultsBox">
            <div
              className={[
                "headline",
                data?.decision === "the SAT" ? "active" : "",
              ].join(" ")}
            >
              {data?.decision === "the SAT" && (
                <FontAwesomeIcon
                  color={BrandColors.brandDarkGreen}
                  icon={faCheck}
                />
              )}
              SAT Total Score: <span>{data?.sat_summary?.sat_score}</span>
              {/* <span>({data?.sat_summary?.act_score} ACT)</span> */}
            </div>
            <div className="subheadline">
              Your SAT Total Score is the sum of your scores on the Reading and
              Writing and the Math portions of the SAT.
            </div>
            <div className="resultsTable active">
              <div className="resultsRow">
                <p>Reading and Writing</p>
                <p>{data?.sat_summary?.details.verbal}</p>
              </div>
              <div className="resultsRow">
                <p>Math</p>
                <p>{data?.sat_summary?.details.math}</p>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <div className="answersMatrix">
          <div className="headline">
            SAT <span>Answers</span>
          </div>
          <div className="subheadline">
            Below is a detailed accounting of your performance on each of the
            sections of the SAT portion of our diagnostic test. In each section,
            you’ll see the number of each question, the correct answer, your
            answer, and the Question Category it belongs to.
          </div>

          <div className="matrixTable two-col">
            <div className="matrixColumn sat">
              <div className="matrixHeadline">
                <p>
                  Reading and Writing:{" "}
                  <span>{data?.sat_summary?.details.verbal}</span>
                </p>
              </div>
              <div className="matrixHeadline subheadline">Module 1</div>
              <div className="matrixData first-row">
                <div className="matrixDataCol">
                  <p>Question #</p>
                </div>
                <div className="matrixDataCol">
                  <p>Your Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Correct Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Question Category</p>
                </div>
              </div>
              {data?.sat_answers?.verbal.map(
                (item: AnswerRow, index: number) => {
                  return (
                    <div key={index} className="matrixData">
                      <div className="matrixDataCol">
                        <p>{index + 1}</p>
                      </div>
                      {item.answer ? (
                        <div className="matrixDataCol">
                          <p>{item.answer}</p>
                        </div>
                      ) : (
                        <div className="matrixDataCol unanswered">
                          <p>{item.answer}</p>
                        </div>
                      )}
                      {item.correct !== item.answer ? (
                        <div className="matrixDataCol incorrect">
                          <p>{item.correct}</p>
                        </div>
                      ) : (
                        <div className="matrixDataCol">
                          <FontAwesomeIcon
                            color={BrandColors.brandDarkGreen}
                            icon={faCheck}
                          />
                        </div>
                      )}

                      <div className="matrixDataCol">
                        <p>{item.type}</p>
                      </div>
                    </div>
                  );
                },
              )}
              <div className="matrixHeadline subheadline">Module 2</div>
              <div className="matrixData first-row">
                <div className="matrixDataCol">
                  <p>Question #</p>
                </div>
                <div className="matrixDataCol">
                  <p>Your Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Correct Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Question Category</p>
                </div>
              </div>
              {data?.sat_answers?.verbal2.map(
                (item: AnswerRow, index: number) => {
                  return (
                    <div key={index} className="matrixData">
                      <div className="matrixDataCol">
                        <p>{index + 1}</p>
                      </div>
                      {item.answer ? (
                        <div className="matrixDataCol">
                          <p>{item.answer}</p>
                        </div>
                      ) : (
                        <div className="matrixDataCol unanswered">
                          <p>{item.answer}</p>
                        </div>
                      )}
                      {item.correct !== item.answer ? (
                        <div className="matrixDataCol incorrect">
                          <p>{item.correct}</p>
                        </div>
                      ) : (
                        <div className="matrixDataCol">
                          <FontAwesomeIcon
                            color={BrandColors.brandDarkGreen}
                            icon={faCheck}
                          />
                        </div>
                      )}

                      <div className="matrixDataCol">
                        <p>{item.type}</p>
                      </div>
                    </div>
                  );
                },
              )}
            </div>

            <div className="matrixColumn sat">
              <div className="matrixHeadline">
                <p>
                  Math: <span>{data?.sat_summary?.details.math}</span>
                </p>
              </div>
              <div className="matrixHeadline subheadline">Module 1</div>
              <div className="matrixData first-row">
                <div className="matrixDataCol">
                  <p>Question #</p>
                </div>
                <div className="matrixDataCol">
                  <p>Your Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Correct Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Question Category</p>
                </div>
              </div>
              {data?.sat_answers?.math.map((item: AnswerRow, index: number) => {
                return (
                  <div className="matrixData" key={Math.random()}>
                    <div className="matrixDataCol">
                      <p>{index + 1}</p>
                    </div>
                    {item.answer ? (
                      <div className="matrixDataCol">
                        <p>{item.answer}</p>
                      </div>
                    ) : (
                      <div className="matrixDataCol unanswered">
                        <p>{item.answer}</p>
                      </div>
                    )}
                    {item.correct !== item.answer ? (
                      <div className="matrixDataCol incorrect">
                        <p>{item.correct}</p>
                      </div>
                    ) : (
                      <div className="matrixDataCol">
                        <FontAwesomeIcon
                          color={BrandColors.brandDarkGreen}
                          icon={faCheck}
                        />
                      </div>
                    )}

                    <div className="matrixDataCol">
                      <p>{item.type}</p>
                    </div>
                  </div>
                );
              })}
              <div className="matrixHeadline subheadline">Module 2</div>
              <div className="matrixData first-row">
                <div className="matrixDataCol">
                  <p>Question #</p>
                </div>
                <div className="matrixDataCol">
                  <p>Your Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Correct Answer</p>
                </div>
                <div className="matrixDataCol">
                  <p>Question Category</p>
                </div>
              </div>
              {data?.sat_answers?.math2.map(
                (item: AnswerRow, index: number) => {
                  return (
                    <div className="matrixData" key={Math.random()}>
                      <div className="matrixDataCol">
                        <p>{index + 1}</p>
                      </div>
                      {item.answer ? (
                        <div className="matrixDataCol">
                          <p>{item.answer}</p>
                        </div>
                      ) : (
                        <div className="matrixDataCol unanswered">
                          <p>{item.answer}</p>
                        </div>
                      )}
                      {item.correct !== item.answer ? (
                        <div className="matrixDataCol incorrect">
                          <p>{item.correct}</p>
                        </div>
                      ) : (
                        <div className="matrixDataCol">
                          <FontAwesomeIcon
                            color={BrandColors.brandDarkGreen}
                            icon={faCheck}
                          />
                        </div>
                      )}

                      <div className="matrixDataCol">
                        <p>{item.type}</p>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        </div>

        <div className="summary-table-headline">
          <h4>Performance Summary</h4>
          <p>
            Below is a detailed accounting of how well you performed on each of
            the Question Categories on our diagnostic test. This will help you
            determine the specific skills and question types you’ll need to
            improve upon to get to mastery.
          </p>
        </div>
        <div className="summary-table-holder">
          <div className="column">
            {data.summary_tables_1.map((item: any) => {
              if (!item)
                return <div className="summaryTable" key={Math.random()}></div>;
              return (
                <div className="summaryTable" key={Math.random()}>
                  <div className="headline">{item.table_name}</div>
                  <div className="tableHolder">
                    {Object.keys(item?.categories).map((row: any) => {
                      return (
                        <div className="tableRow">
                          <div className="tableCol donut">
                            <CircularProgressbar
                              value={item.categories[row].value}
                              maxValue={100}
                              text={`${Math.round(
                                item.categories[row].value,
                              )}%`}
                              styles={buildStyles({
                                textSize: "16px",
                                textColor: "#fff",
                                backgroundColor: "#cc9900",
                                trailColor: "#686868",
                                pathColor: "#bad7a7",
                              })}
                              strokeWidth={12}
                            />
                          </div>
                          <div className="tableCol">
                            <p className="catName">
                              <Tooltip
                                className="tooltip"
                                title={TooltipText(
                                  questionCatDescriptions[
                                    item.categories[row]
                                      .title as keyof typeof questionCatDescriptions
                                  ],
                                )}
                                placement="top"
                              >
                                <p> {item.categories[row].title}</p>
                              </Tooltip>
                            </p>
                          </div>
                          <div className="tableCol">
                            <p>
                              # of Correct:{" "}
                              <span>{item.categories[row].correct}</span>
                            </p>
                          </div>
                          <div className="tableCol">
                            <p>
                              # of Incorrect:{" "}
                              <span>{item.categories[row].incorrect}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <NetworkError open={hasError} />
    </div>
  );
};

export default ExamResults;
